<template>
  <div>
    <div class="mb-[2px] flex items-baseline gap-x-2">
      <div class="text-[15px] font-semibold text-gpx_black-500">
        {{ name }}
      </div>
      <div class="flex items-center">
        <UICounter :count="count" />
        <UIIconButton
          v-if="getUIButtonActions()?.length > 0"
          class="absolute right-[9px] top-[11px] ml-[6px] md:right-[16px]"
          icon="EllipsisVerticalIcon"
          @click="$emit('setEditItemId', props.group?.id)"
        >
          <UIActions
            v-if="props.editItemId === props.group?.id"
            :id="'group-actions-' + props.group?.id"
            v-click-outside="() => $emit('resetEditItemId')"
            :editItemId="props.editItemId"
            :actions="getUIButtonActions()"
          />
        </UIIconButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import {
  COMMON_DELETE_ERROR,
  COMMON_DELETE_SUCCESS,
  MESSAGE403,
} from '@/config/constants';
import { useGroup } from '@/composables/useGroup.ts';
import { useAbility } from "@casl/vue";
import UIIconButton from "@/components/ui/UIIconButton.vue";

defineEmits(['setEditItemId', 'resetEditItemId']);
const props = defineProps({
  group: Object,
  editItemId: Number,
});
const store = useStore();
const toast = useToast();

const { getById, deleteGroup, fetchByAccountIdAndId } = useGroup();

const accountId = store.getters['auth/accountId'];
const name = computed(() => props.group?.name);
const count = computed(() => props.group?.devices_count);
const { can } = useAbility();

function getUIButtonActions() {
  if (!can('manage', 'Groups')) return [];

  const editGroupAction = {
    icon: 'PencilSquareIcon',
    label: 'Edit',
    action: {
      mode: 'fn',
      fn: async (id) => {
        if (!can('manage', 'Groups')) {
          toast.error(MESSAGE403);
          return;
        }
        // The group list endpoint is fetched with exclude_devices: 1, so
        // group.device_ids is missing. Hydrate the single group before opening
        // the modal so existing members are pre-selected and a swap doesn't
        // wipe them.
        await fetchByAccountIdAndId(accountId, id);
        const group = getById(id);
        store.dispatch('modal/open', {
          name: 'DeviceGroupModal',
          params: {
            actionType: 'swap',
            id,
            name: group.name,
            device_ids: group.device_ids,
          },
        });
      },
    },
  };

  const deleteGroupAction = {
    icon: 'TrashIcon',
    label: 'Delete',
    action: {
      mode: 'fn',
      fn: (id) => {
        if (!can('manage', 'Groups')) {
          toast.error(MESSAGE403);
          return;
        }
        const group = getById(id);
        store.dispatch('modal/open', {
          name: 'CommonConfirmationModal',
          params: {
            actionLabel: 'Delete Group',
            title: group.name,
            body: 'Are you sure you want to delete this group?',
            onOk: () => deleteGroup(accountId, id),
            successMessage: COMMON_DELETE_SUCCESS,
            errorMessage: COMMON_DELETE_ERROR,
          },
        });
      },
    },
  };
  return [editGroupAction, deleteGroupAction];
}
</script>
